/**
 * Edit preset form state - items, selection, bulk, reorder, remove
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toClickIdStructure, toDomainScheme } from '@trafficpulse/shared';

import type { Preset, PresetItem, PresetItemPayload } from '../shared';
import type { PresetsModalCatalog } from '../shared';

export type EditableItem = PresetItemPayload & { id: string };

function toEditableItem(item: PresetItem): EditableItem {
  return {
    id: item.id,
    routeId: item.routeId,
    campaignWiserId: item.campaignWiserId ?? undefined,
    leadsListWiserId: item.leadsListWiserId,
    shortLinkTldValue: item.shortLinkTldValue,
    amount: item.amount,
    batchAmount: item.batchAmount ?? undefined,
    batchDelay: item.batchDelay ?? undefined,
    forceSend: item.forceSend ?? false,
    aiOptimizationEnabled: item.aiOptimizationEnabled ?? false,
    aiOptimizationType:
      item.aiOptimizationType === 'ROI' || item.aiOptimizationType === 'CTR'
        ? item.aiOptimizationType
        : undefined,
    aiOptimizationBatchAmount: item.aiOptimizationBatchAmount ?? undefined,
    aiOptimizationMinimalValuePercent: item.aiOptimizationMinimalValuePercent ?? undefined,
    aiOptimizationSendUntil: item.aiOptimizationSendUntil ?? undefined,
    useOnlyNewDomains: item.useOnlyNewDomains ?? false,
    clickIdStructure: toClickIdStructure(item.clickIdStructure),
    domainScheme: toDomainScheme(item.domainScheme),
    staticTagId: item.staticTagId ?? undefined,
    senderNames: item.senderNames ?? undefined,
    smsTexts: item.smsTexts ?? undefined,
    rotators: item.rotators ?? undefined,
  };
}

function toPayloadItem(item: EditableItem): PresetItemPayload {
  const { id: _id, ...rest } = item;
  return rest;
}

export function useEditPresetForm(preset: Preset | null, _catalog: PresetsModalCatalog) {
  const [name, setName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [items, setItems] = useState<EditableItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (preset) {
      setName(preset.name);
      setGroupName(preset.groupName ?? '');
      setItems(preset.items.map(toEditableItem));
      setSelectedIds(new Set());
    }
  }, [preset]);

  const updateItem = useCallback((id: string, patch: Partial<EditableItem>) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    );
  }, []);

  const applyBulk = useCallback((patch: Partial<EditableItem>) => {
    const ids = selectedIdsRef.current;
    setItems((prev) =>
      prev.map((it) => (ids.has(it.id) ? { ...it, ...patch } : it)),
    );
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(itemsRef.current.map((i) => i.id)));
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const toggleSelectAll = useCallback(() => {
    const ids = itemsRef.current.map((i) => i.id);
    const allSelected = ids.length > 0 && selectedIdsRef.current.size === ids.length;
    setSelectedIds(allSelected ? new Set() : new Set(ids));
  }, []);

  const reorder = useCallback((fromIdx: number, toIdx: number) => {
    setItems((prev) => {
      const arr = [...prev];
      const [removed] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, removed!);
      return arr;
    });
  }, []);

  const removeSelectedItems = useCallback(() => {
    const ids = selectedIdsRef.current;
    if (ids.size === 0) return;
    setItems((prev) => prev.filter((it) => !ids.has(it.id)));
    setSelectedIds(new Set());
  }, []);

  const buildPayload = useCallback((): {
    name: string;
    groupName?: string;
    items: PresetItemPayload[];
  } => ({
    name: name.trim(),
    groupName: groupName.trim() || undefined,
    items: items.map(toPayloadItem),
  }), [name, groupName, items]);

  return {
    name,
    setName,
    groupName,
    setGroupName,
    items,
    selectedIds,
    updateItem,
    applyBulk,
    toggleSelect,
    selectAll,
    clearSelection,
    toggleSelectAll,
    reorder,
    removeSelectedItems,
    buildPayload,
  };
}
